import { useEffect, useMemo } from 'react';

/** A committed React lifetime owns its requests; stale closures cannot acquire a new one. */
export function useRequestOwner(source: object | null, selection: boolean | number) {
  const owner = useMemo(() => {
    let active = false;
    let current: AbortController | null = null;
    return {
      activate() {
        active = true;
      },
      begin() {
        if (!active) return null;
        current?.abort();
        current = new AbortController();
        return current;
      },
      owns(request: AbortController) {
        return active && current === request && !request.signal.aborted;
      },
      cancel() {
        if (!active) return false;
        current?.abort();
        current = null;
        return true;
      },
      dispose() {
        active = false;
        current?.abort();
        current = null;
      },
    };
  }, [source, selection]);
  useEffect(() => {
    owner.activate();
    return () => owner.dispose();
  }, [owner]);
  return owner;
}
